import React, { useMemo } from 'preact/compat';

import PathChangeContext from 'Flyout/Contexts';
import Node from 'Flyout/components/Node';
import Search from 'Flyout/components/Search';

export default class Navigation extends React.Component {
    constructor(props) {
        super(props);

        // set some variables from the props that never change again
        const {
            api, cssClass, navId, internav,
        } = (props);
        this.api = api;
        this.cssClass = cssClass;
        this.navId = navId;
        this.internav = internav;

        this.state = {
            // the currently opened object in the navigation (via URL)
            currentObject: this.api.activeObject,
            // the object that will be opened next - will differ from activeObject while animations are running
            nextObject: this.api.activeObject,
            // the object that represents the page (or its nearest ancestor) that the user is on
            activeObject: this.api.activeObject,
        };

        // reference to the DOM element of the active element, will be set by the corresponding Node component instance
        this.activeElem = null;

        this.setNext = this.setNext.bind(this);
        this.setCurrent = this.setCurrent.bind(this);
        // internav bindings
        this.toggleNavigation = this.toggleNavigation.bind(this);
    }

    /**
     * This hook gets called when the App has been mounted.
     * Inform the API to update the tree and then set the current object.
     */
    componentDidMount() {
        // update the object tree
        this.api.updateTree()
            .then(() => {
                // set the current object
                this.resetCurrent();
                // inform the parent, that this navigation has loaded
                this.internav.internavLoaded();
            });
    }

    /**
     * Reset current object when the navigation or the complete flyout are not open.
     * This means that whenever the navigation gets opened, it reverts to the initial current object.
     */
    componentDidUpdate(prevProps) {
        if (!this.props.flyoutOpen || !this.props.open) {
            this.resetCurrent();
        }

        // if the navigation panel reopens and this is the open navigation,
        // scroll to the active element
        if (!prevProps.flyoutOpen && this.props.flyoutOpen && this.props.open && this.activeElem) {
            // scroll after the next render, so we are sure the browser can actually scroll
            requestAnimationFrame(() => {
                this.activeElem.scrollIntoView({ behavior: 'instant', block: 'center' });

                // focus the active element's interactive child
                const interactive = this.activeElem.querySelector('a');
                if (interactive) {
                    interactive.focus();
                }
            });
        }
    }

    /**
     * Set the object that will be opened next
     * @param {TreeObject} treeObject
     */
    setNext(treeObject) {
        this.setState({
            nextObject: treeObject,
        });
        this.toggleNavigation(true);
    }

    /**
     * Set the nextObject as the current a.k.a. "animations are done"
     */
    setCurrent() {
        if (this.state.currentObject !== this.state.nextObject) {
            this.setState((prevState) => ({
                currentObject: prevState.nextObject,
            }));
        }
    }

    /**
     * Open / close our Navigation.
     */
    toggleNavigation(open) {
        this.internav.internavOpen(open ? this.navId : null);
    }

    /**
     * set the state to the new active object
     */
    resetCurrent() {
        // get active object from API
        const target = this.api.activeObject;
        // leaf nodes will not be set as current, but their parents
        const current = (target.children.length) ? target : target.parent || target;
        if (this.state.currentObject !== current) {
            // save current and target into the state
            this.setState({
                currentObject: current,
                nextObject: current,
                activeObject: target,
            });
        }
    }

    render() {
        const context = useMemo(() => ({
            setNext: this.setNext,
            setCurrent: this.setCurrent,
            toggleNavigation: this.toggleNavigation,
            activeElem: (elem) => { this.activeElem = elem; },
        }), [this.setNext, this.setCurrent, this.toggleNavigation]);

        const root = this.state.currentObject.breadcrumbs[0];

        // Remember the relevant information in a treeState that is simply pushed through
        // the whole component tree as it is used nearly everywhere.
        const treeState = {
            currentObject: this.state.currentObject,
            nextObject: this.state.nextObject,
            activeObject: this.state.activeObject,
            treeOpen: this.props.open,
        };

        const search = (!this.api.searchEnabled) ? null : (
            <Search api={this.api} toggleNavigation={this.toggleNavigation} />
        );

        return (
            <PathChangeContext value={context}>
                {this.props.visible ? (
                    <Node object={root} treeState={treeState} className={this.cssClass}>
                        { search }
                    </Node>
                ) : null}
            </PathChangeContext>
        );
    }
}
